/**
 * Lazy loader for Plaid Link (DL-09). Plaid Link is a hosted JS SDK, not an npm dependency — we inject
 * its CDN script on first use and resolve the global `Plaid` handle. Returns null when the script can't
 * load (offline / blocked), so the caller can degrade gracefully with a clear message.
 *
 * Web-only in v1 (the desktop/mobile Tauri shells connect via the web app).
 */
export interface PlaidHandler {
  open(): void;
  exit(): void;
}

export interface PlaidLinkOptions {
  token: string;
  onSuccess: (publicToken: string, metadata: unknown) => void;
  onExit?: (err: unknown, metadata: unknown) => void;
  /** Set on the OAuth return page to resume a redirect-based (OAuth bank) Link flow. */
  receivedRedirectUri?: string;
}

export interface PlaidGlobal {
  create(options: PlaidLinkOptions): PlaidHandler;
}

const PLAID_SCRIPT_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

/** The registered Plaid OAuth redirect path (must match `PLAID_REDIRECT_URI` + the Plaid dashboard). */
export const PLAID_REDIRECT_PATH = '/plaid-oauth';

/**
 * OAuth banks redirect the whole tab away and back, so the `link_token` must survive the navigation.
 * We stash it in localStorage when opening Link and read it back on the return page.
 */
const LINK_TOKEN_KEY = 'lapo.plaid.linkToken';
/**
 * How long a stashed link token stays "live". A hosted-link OAuth realistically completes within a few
 * minutes; anything older is an abandoned attempt. Bounding it stops a stale token from re-arming the
 * "pending connect" UI on every app launch (the buttons were showing with no connection in progress).
 */
const LINK_TOKEN_TTL_MS = 15 * 60_000;

export function storeLinkToken(token: string): void {
  try {
    // Preserve the original timestamp across redundant re-stashes so the TTL measures the true age.
    let ts = Date.now();
    const raw = localStorage.getItem(LINK_TOKEN_KEY);
    if (raw) {
      try {
        const prev = JSON.parse(raw) as { token?: string; ts?: number };
        if (prev?.token === token && typeof prev.ts === 'number') ts = prev.ts;
      } catch {
        /* legacy bare-string value → fresh timestamp */
      }
    }
    localStorage.setItem(LINK_TOKEN_KEY, JSON.stringify({ token, ts }));
  } catch {
    /* storage may be unavailable (private mode) — OAuth resume just won't work */
  }
}

/** Read the stashed link token, applying the TTL. `consume: true` removes a valid token (single-use);
 *  `consume: false` leaves a valid token in place so refocus retries can keep using it. Either way a
 *  stale/legacy token is cleared. */
function readStoredLinkToken(consume: boolean): string | null {
  try {
    const raw = localStorage.getItem(LINK_TOKEN_KEY);
    if (!raw) return null;
    let token: string | null = null;
    let ts = 0;
    try {
      const parsed = JSON.parse(raw) as { token?: string; ts?: number };
      if (parsed && typeof parsed.token === 'string') {
        token = parsed.token;
        ts = typeof parsed.ts === 'number' ? parsed.ts : 0;
      }
    } catch {
      token = null; // legacy bare string (no timestamp) — can't age it, so treat as stale and clear it
    }
    // No usable timestamp, or older than the TTL → an abandoned attempt: clear it and report none.
    if (!token || !ts || Date.now() - ts > LINK_TOKEN_TTL_MS) {
      clearStoredLinkToken();
      return null;
    }
    if (consume) clearStoredLinkToken();
    return token;
  } catch {
    return null;
  }
}

/** Read and REMOVE the stashed link token (single-use consumers: OAuth resume, cold-start restash). */
export function takeStoredLinkToken(): string | null {
  return readStoredLinkToken(true);
}

/** Read the stashed link token WITHOUT removing it, so a refocus finisher can retry until it resolves
 *  (or the TTL expires). Success paths call {@link clearStoredLinkToken} to end the retry loop. */
export function peekStoredLinkToken(): string | null {
  return readStoredLinkToken(false);
}

export function clearStoredLinkToken(): void {
  try {
    localStorage.removeItem(LINK_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** True when the page was loaded as a Plaid OAuth redirect return (has an `oauth_state_id`). */
export function isPlaidOAuthReturn(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === PLAID_REDIRECT_PATH && /[?&]oauth_state_id=/.test(window.location.search);
}

declare global {
  interface Window {
    Plaid?: PlaidGlobal;
  }
}

let loading: Promise<PlaidGlobal | null> | null = null;

/** Resolve the global Plaid Link SDK, injecting its script on first call. Cached across calls. */
export function loadPlaidLink(): Promise<PlaidGlobal | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.Plaid) return Promise.resolve(window.Plaid);
  if (loading) return loading;
  loading = new Promise<PlaidGlobal | null>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PLAID_SCRIPT_SRC}"]`);
    const onLoad = (): void => resolve(window.Plaid ?? null);
    const onError = (): void => {
      loading = null;
      resolve(null);
    };
    if (existing) {
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });
      if (window.Plaid) resolve(window.Plaid);
      return;
    }
    const script = document.createElement('script');
    script.src = PLAID_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    document.head.appendChild(script);
  });
  return loading;
}
